import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Linkedin, Clock3, ArrowRight } from "lucide-react";
import { BRAND_ASSETS } from "@/lib/brandAssets";

const LOGO_URL = BRAND_ASSETS.logo;

export const Footer = () => {
  return (
    <footer className="footer-dark text-white relative" data-testid="footer">
      <div className="safety-stripe" />
      <div className="noise-overlay" />
      
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="Junk In Da Truck" className="h-14 w-auto rounded-lg" loading="lazy" />
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              Expert junk removal & hauling services in Vero Beach, FL. Fast, affordable cleanup for homes & businesses.
            </p>
            <div className="flex gap-3">
              <a href="https://www.facebook.com/JunkInDaTruck/" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#97BF04] hover:text-[#0D0D0D] transition-colors" data-testid="footer-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://instagram.com/junkindatruck" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#97BF04] hover:text-[#0D0D0D] transition-colors" data-testid="footer-instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://x.com/JunkInDaTruck" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#97BF04] hover:text-[#0D0D0D] transition-colors" data-testid="footer-twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="mailto:info@junkindatruck.com" 
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-[#97BF04] hover:text-[#0D0D0D] transition-colors" data-testid="footer-email-social">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Contact Information</h3>
            <div className="space-y-3">
              <a href="tel:+17724945048" className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#97BF04] transition-colors" data-testid="footer-phone">
                <Phone className="w-4 h-4 text-[#97BF04]" /> +1 (772) 494-5048
              </a>
              <a href="mailto:info@junkindatruck.com" className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#97BF04] transition-colors" data-testid="footer-email">
                <Mail className="w-4 h-4 text-[#97BF04]" /> info@junkindatruck.com
              </a>
              <a href="https://maps.google.com/?q=Vero+Beach,+FL+32960" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#97BF04] transition-colors" data-testid="footer-address">
                <MapPin className="w-4 h-4 text-[#97BF04]" /> Vero Beach, FL 32960
              </a>
              <div className="flex items-start gap-3 text-sm text-gray-400" data-testid="footer-hours">
                <Clock3 className="w-4 h-4 text-[#97BF04] mt-0.5" />
                <div className="space-y-1">
                  <div>Monday: 7:00 AM - 7:00 PM</div>
                  <div>Tuesday: 7:00 AM - 7:00 PM</div>
                  <div>Wednesday: 7:00 AM - 7:00 PM</div>
                  <div>Thursday: 7:00 AM - 7:00 PM</div>
                  <div>Friday: 7:00 AM - 7:00 PM</div>
                  <div>Saturday: 7:00 AM - 7:00 PM</div>
                  <div>Sunday: By Appointment</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Quick Links</h3>
            <div className="space-y-2">
              {[
                { label: "Residential Junk Removal", href: "/services/residential-junk-removal" },
                { label: "Commercial Services", href: "/services/office-cleanout" },
                { label: "Junk Hauling", href: "/junk-hauling" },
                { label: "Service Areas", href: "/service-areas" },
                { label: "Our Work", href: "/gallery" },
                { label: "Reviews", href: "/testimonials" },
                { label: "Blog", href: "/blog" },
                { label: "FAQ", href: "/faq" },
              ].map((link) => (
                <Link key={link.label} to={link.href} className="block text-sm text-gray-400 hover:text-[#97BF04] transition-colors">
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="mt-6">
              <a
                href="/estimate"
                className="inline-flex items-center gap-2 rounded-xl bg-[#97BF04] px-5 py-3 text-sm font-semibold text-[#0D0D0D] hover:bg-[#83A603] transition-colors shadow-lg"
                data-testid="footer-estimate-button"
              >
                Get a Quote <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
            <p className="text-xs text-gray-500">&copy; {new Date().getFullYear()} Junk In Da Truck. All rights reserved.</p>
            <a
              href="https://www.spotlightdigitalfl.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-[#97BF04] transition-colors"
            >
              Base website design by Spotlight Digital FL
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-600">Licensed & Insured</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
